<template>
  <div>
    <headline-container headline="Create Order" :breadcrumbs="breadcrumbs"></headline-container>

    <div class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Create Order
            </h3>

            <p>
              Enter the details of the order.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <form @submit.prevent="submit" class="form-horizontal">
            <div class="panel panel-default">
              <loading-spinner v-if="loading"></loading-spinner>

              <div v-else class="panel-body">
                <TextFormGroup
                    v-model="order.title"
                    name="title"
                    label="Title"
                    help-text="The title of the order."
                    :error="getError('title')"
                />

                <TextFormGroup
                    v-model="order.purchase_order"
                    name="purchase_order"
                    label="Purchase Order (PO number)"
                    help-text="The company's purchase order (not required)."
                    :error="getError('purchase_order')"
                />

                <TypeaheadFormGroup
                    v-model="order.company_id"
                    name="company_id"
                    label="Company"
                    help-text="The company that is placing the order."
                    :error="getError('company_id')"
                    endpoint="/api/v2/companies"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :result-label-property="row => row.attributes.name"
                />

                <SelectFormGroup
                    v-model="order.user_id"
                    name="user_id"
                    label="User"
                    help-text="The user that is responsible for the order."
                    :error="getError('user_id')"
                    :options="userOptions"
                />

                <SelectWithButtonFormGroup
                    v-model="order.contact_person_id"
                    name="contact_person_id"
                    label="Contact Person"
                    help-text="The contact person in the company."
                    :error="getError('contact_person_id')"
                    :options="personOptions"
                    :disabled="loadingPeople || personOptions.length === 0"
                    @click="openPersonModal"
                />

                <TypeaheadFormGroup
                    v-model="order.content_responsible_id"
                    name="content_responsible_id"
                    label="Content Responsible"
                    help-text="The person that is responsible for the content of the ads."
                    :error="getError('content_responsible_id')"
                    endpoint="/api/v2/people"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :result-label-property="person => person.attributes.name + (person.attributes.company_id ? ' (' + person.attributes.company_name + ')' : '')"
                    :initial-label="contentResponsibleName"
                />

                <TypeaheadFormGroup
                    v-model="order.other_payer_id"
                    name="other_payer_id"
                    label="Other Payer"
                    help-text="The company that pays for the ad (if not the company itself)."
                    :error="getError('other_payer_id')"
                    endpoint="/api/v2/companies"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :result-label-property="row => row.attributes.name"
                />

                <hr>

                <SubmitFormGroup button-class="btn-black"/>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <CreatePersonModal :company-id="order.company_id" @created="personCreated"/>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import store from '@/store'
import CreatePersonModal from '@/pages/Orders/Orders/Create/Modals/CreatePerson'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingSpinner from '@/components/Layout/LoadingSpinner'
import HeadlineContainer from '@/components/Layout/HeadlineContainer'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SelectWithButtonFormGroup from '@/components/FormGroups/Horizontal/SelectWithButton'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TypeaheadFormGroup from '@/vendor/forms/FormGroups/Horizontal/Typeahead'

export default {
  components: {
    CreatePersonModal,
    HelpText,
    LoadingSpinner,
    HeadlineContainer,
    SelectFormGroup,
    SelectWithButtonFormGroup,
    SubmitFormGroup,
    TextFormGroup,
    TypeaheadFormGroup
  },

  mixins: [Errors, Load],

  data() {
    return {
      shared: store,
      order: {
        company_id: null,
        contact_person_id: null,
        user_id: store.user.id
      },
      users: [],
      people: [],
      contentResponsibleName: null,
      loadingPeople: false
    }
  },

  mounted() {
    this.load({users: '/api/v2/users'})
  },

  watch: {
    'order.company_id'() {
      this.loadingPeople = true

      this.order.contact_person_id = null

      http.get('/api/v2/people', {'filter[company_id]': this.order.company_id})
          .then(response => {
            this.people = response.data.data

            this.loadingPeople = false
          })
    }
  },

  computed: {
    breadcrumbs() {
      return [
        {url: '/orders', text: 'Orders'},
        {text: 'Create'}
      ]
    },

    userOptions() {
      return this.users.data.map(user => {
        return {
          value: user.id,
          label: user.attributes.full_name
        }
      })
    },

    personOptions() {
      return this.people.map(person => {
        return {
          value: person.id,
          label: person.attributes.name
        }
      })
    }
  },

  methods: {
    submit() {
      http.post('/api/v1/orders', this.order)
          .then(response => {
            notify.success('The order was saved')

            this.$router.push('/companies/' + response.data.data.company_id + '/orders/' + response.data.data.id)
          })
          .catch(error => {
            if (error.response.status === 400) {
              return notify.error(error.response.data.message)
            }

            throw error
          })
          .catch(this.catchValidationErrors)
    },

    openPersonModal() {
      $('#create-person-modal').modal('show')
    },

    personCreated(person) {
      this.people.push(person)

      this.people.sort((a, b) => a.attributes.name.toLowerCase() >= b.attributes.name.toLowerCase() ? 1 : -1)

      this.$nextTick(() => {
        this.order.contact_person_id = person.id
      })
    }
  }
}
</script>
